import Vue from 'vue';
import { getModule } from 'vuex-module-decorators';
import { Component } from 'vue-property-decorator';
import ScreenSizeHelper, { EScreenSize, MOBILE_USER_AGENT_REGEX } from '@/helpers/screen-size';
import { EUserFlag, IBannerMessage, IStreamStats, IUser, IUserFlag, IUserIdentity, TStreamStatus } from '@/helpers/user';
import {
  TSubscriptionPlanType,
  ISubscription,
  ESubscriptionStatus,
  IPaymentMethod,
  TSubscriptionStep,
} from '@/helpers/subscription';
import UserModule from '@/store/modules/user';
import SLIDModule from '@/store/modules/slid';
import CheckoutModule from '@/store/modules/checkout';
import UtilHelper from '@/helpers/utils';
import { EModalName } from '@/helpers/modal';
import HttpHelper from '@/helpers/http';
import { ESocketEvent } from '@/helpers/socket';
import { cloneDeep } from 'lodash';
import UserApi from '@/apis/user-api';
import AnalyticHelper from '@/helpers/analytic';
import { connectToStreamlabsId } from '@/helpers/streamlabs-identity';
import { EPlatformNames, IConnectedAccount } from '@/helpers/platforms';
import StreamApi from '@/apis/stream-api';

@Component({})
export default class Base extends Vue {
  $http: typeof HttpHelper;
  $sla: typeof AnalyticHelper;

  userModule = getModule(UserModule, this.$store);
  slidModule = getModule(SLIDModule, this.$store);
  checkoutModule = getModule(CheckoutModule, this.$store);

  previewBaseUrl: string = process.env.MIX_PREVIEW_SERVER_URL;
  validRouterRedirects = [
    '/account',
    '/projects'
  ];

  get screenSize(): EScreenSize {
    return ScreenSizeHelper.screenSize;
  }

  get isMobile(): boolean {
    return MOBILE_USER_AGENT_REGEX.test(navigator.userAgent) || this.screenSize <= EScreenSize.mobile;
  }

  get ctaText(): string {
    return !!this.currentUser ? 'Get Started' : '7-Day Free Trial';
  }

  get currentUser(): IUser {
    return this.userModule.user;
  }

  get availablePlatforms(): string[] {
    return this.currentUser?.available_platforms || [EPlatformNames.twitch];
  }

  get connectedAccounts(): any[] {
    return this.currentUser?.connected_accounts || [];
  }

  get isActingAs(): boolean {
    return !!this.userModule.user?.original_username;
  }

  set currentUser(user: IUser) {
    this.userModule.setUser(user);
  }

  get userSLWidgetToken(): string {
    return this.currentUser?.widget_token || null;
  }

  set userSLWidgetToken(widgetToken: string) {
    const user = cloneDeep(this.currentUser);
    user.widget_token = widgetToken;
    this.userModule.setUser(user);
  }

  get userFlags(): IUserFlag {
    return this.userModule.userFlags;
  }

  get livePlatforms(): string[] {
    return this.userModule.livePlatforms;
  }

  get hasLivePlatforms(): boolean {
    return !!this.livePlatforms.length;
  }

  setUserFlags(userFlags: IUserFlag) {
    const userFlagsClone = cloneDeep(this.userFlags);

    Object.keys(userFlags).forEach((key) => {
      userFlagsClone[key] = userFlags[key];
    });

    this.userModule.setUserFlags(userFlagsClone);
  }

  removeUserFlag(userFlag: EUserFlag) {
    const userFlagsClone = cloneDeep(this.userFlags);
    delete userFlagsClone[userFlag];

    this.userModule.setUserFlags(userFlagsClone);
  }

  async loadUser(forceReload = false) {
    if (this.userModule.isAuthenticated && !forceReload) return Promise.resolve();
    const user = await UserApi.getMe();

    if (user) {
      const userFlag = await UserApi.getUserFlags();
      const userSubscription = await UserApi.getUserSubscription();
      await this.userModule.setUser(user);
      await this.userModule.setUserFlags(userFlag.flags);
      await this.userModule.setSubscription(userSubscription?.subscription);

      const userCsrfToken = await UserApi.getCsrfToken();
      HttpHelper.setCsrfToken(userCsrfToken);

      // if (!user.has_consented_terms) this.$modal.show(EModalName.terms);
    }
  }

  async loadLastStreamSessionPlatforms() {
    setTimeout(async () => {
      const response = await StreamApi.getLastStreamSessionDestinations();
      this.userModule.setLivePlatforms(response.platforms);
    }, 3000);
  }

  async loadStreamStatus() {
    if (!this.currentUser) return;

    const response = await UserApi.getStreamStatus();
    await this.userModule.setStreamStatus(response.status);
  }

  get streamStatus(): TStreamStatus {
    return this.userModule.streamStatus;
  }

  set streamStatus(status: TStreamStatus) {
    this.userModule.setStreamStatus(status);
  }

  get isLive(): boolean {
    return this.streamStatus === 'Online';
  }

  get isStreamPreview() {
    return this.streamStatus === 'Preview';
  }

  get streamStats(): IStreamStats {
    return this.userModule.streamStats;
  }

  get shouldShowEditorOnboarding(): boolean {
    return !this.userFlags.hasOwnProperty(EUserFlag.editorOnboardingShown);
  }

  get shouldShowProjectOnboarding(): boolean {
    return !this.userFlags.hasOwnProperty(EUserFlag.projectOnboardingShown);
  }

  get showStream(): boolean {
    return this.userModule.showStream;
  }

  set showStream(showStream: boolean) {
    this.userModule.setShowStream(showStream);
  }

  get userSubscription(): ISubscription {
    return this.userModule.subscription;
  }

  get userSubscriptionIsValid(): boolean {
    return this.userSubscription?.is_valid;
  }

  set userSubscription(subscription: ISubscription) {
    this.userModule.setSubscription(subscription);
  }

  get userPaymentMethod(): IPaymentMethod {
    return this.userModule.paymentMethod;
  }

  set userPaymentMethod(paymentMethod: IPaymentMethod) {
    this.userModule.setPaymentMethod(paymentMethod);
  }

  get isSubscriber(): boolean {
    return !!(
      (this.userSubscription &&
        (this.userSubscription.status === ESubscriptionStatus.active ||
          UtilHelper.dateIsFuture(this.userSubscription.expires_at))) || this.userHasUltra
    );
  }

  get selectedPlan(): TSubscriptionPlanType {
    return this.checkoutModule.selectedPlan;
  }

  set selectedPlan(plan: TSubscriptionPlanType) {
    this.checkoutModule.setSelectedPlan(plan);
  }

  get paymentProcessing(): boolean {
    return this.checkoutModule.paymentProcessing;
  }

  set paymentProcessing(paymentProcessing: boolean) {
    this.checkoutModule.setPaymentProcessing(paymentProcessing);
  }

  get plans() {
    return this.checkoutModule.plans;
  }

  set plans(plans) {
    this.checkoutModule.setPlans(plans);
  }

  get hasAgreedToTerms(): boolean {
    return this.checkoutModule.hasAgreedToTerms;
  }

  set hasAgreedToTerms(hasAgreedToTerms: boolean) {
    this.checkoutModule.setHasAgreedToTerms(hasAgreedToTerms);
  }

  get displayAgreeToTermsError(): boolean {
    return this.checkoutModule.displayAgreeToTermsError;
  }

  set displayAgreeToTermsError(displayAgreeToTermsError: boolean) {
    this.checkoutModule.setDisplayAgreeToTermsError(displayAgreeToTermsError);
  }

  get userIdentity(): IUserIdentity {
    return this.currentUser?.identity;
  }

  get userHasUltra(): boolean {
    return this.currentUser?.identity?.ultra;
  }

  get hasTwitch(): boolean {
    return this.currentUser?.hasTwitch;
  }

  get userHasYouTube(): boolean {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.youtube);
  }

  get userHasKick(): boolean {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.kick);
  }

  get userHasTiktok(): boolean {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.tiktok);
  }

  get shouldDisplaySLIDSetup(): boolean {
    return this.currentUser && !this.currentUser.identity;
  }

  get userTwitchAccount(): IConnectedAccount {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.twitch) || null;
  }

  get userYouTubeAccount(): IConnectedAccount {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.youtube) || null;
  }

  get bannerMessage(): IBannerMessage {
    return this.userModule.bannerMessage;
  }

  get userTiktokAccount(): IConnectedAccount {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.tiktok) || null;
  }

  get userKickAccount(): IConnectedAccount {
    return this.currentUser?.connected_accounts.find((account) => account.type === EPlatformNames.kick) || null;
  }

  toggleShowStream() {
    this.showStream = !this.showStream;
  }

  openCheckoutModalMini(step: TSubscriptionStep = null) {
    this.$modal.show(EModalName.checkoutMini, { step });
  }

  openSwitchPlanModal(plan: TSubscriptionPlanType = null) {
    this.$modal.show(EModalName.switchPlan, { plan });
  }

  sendSocketEvent(event: ESocketEvent, arg = null) {
    this.$socket.client.emit(event, arg);
  }

  addSocketEventListener(event: ESocketEvent, callback: (data: any) => void) {
    if (!this.$socket.client.listeners(event).length)
      this.$socket.client.on(event, callback);
  }

  navigateHome() {
    if (this.currentUser) {
      this.$router.push('/projects');
      return;
    }

    this.$router.push('/');
  }

  toPreviousPage(blockExternalRedirect = false) {
    if (blockExternalRedirect && this.userModule.isPreviousPageExternal) {
      return this.navigateHome();
    }

    this.$router.back();
  }

  priceToDollar(price) {
    return (price / 100).toFixed(2);
  }

  async connectSLID() {
    await connectToStreamlabsId();

    this.loadUser(true);
  }

  navigateTo(page: string) {
    const path = `/${page}`;
    if (this.$route.fullPath === path) return;

    if (page.includes('#')) {
      const [pathPart, hashPart] = page.split('#');
      this.$router.push({ path: pathPart, hash: '#' + hashPart });
      return;
    }

    this.$router.push(`/${page}`);
  }

  logout() {
    window.location.href = '/api/v1/logout';
  }
}
